import { useAuth } from '@contexts/auth-provider';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import * as React from 'react';

import { identifyHeapUser, resetHeapIdentity } from '../../heapAnalytics';
import { useCanTrackAnalytics } from '../use-can-track-analytics/useCanTrackAnalytics';
import { buildHeapUserProperties } from './useHeapUserProperties.utils';

/**
 * Owns Heap user identification for the whole application, including remotes.
 *
 * Identity is keyed on the Azure AD account ID, not email — email remains a user
 * property. Microfrontends must not call `identify` or `addUserProperties`;
 * keeping both here means identity and user properties are gated on consent in
 * exactly one place. Event tracking via `heap.track` is deliberately not gated.
 */
export function useHeapUserProperties(): void {
  const { authData, isAuthenticated } = useAuth();
  const { modules, selectedModule } = useModules();
  const { organizations, selectedOrganization } = useOrganizations();
  const canTrackAnalytics = useCanTrackAnalytics();
  const wasAuthenticated = React.useRef(false);
  const previousCanTrackAnalytics = React.useRef<boolean | null>(null);

  const heapUserProperties = React.useMemo(() => {
    if (!authData) {
      return null;
    }

    return buildHeapUserProperties({
      authData,
      modules,
      selectedModule,
      organizations,
      selectedOrganization,
    });
  }, [authData, modules, selectedModule, organizations, selectedOrganization]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      if (wasAuthenticated.current) {
        resetHeapIdentity();
        wasAuthenticated.current = false;
        previousCanTrackAnalytics.current = null;
      }
      return;
    }

    wasAuthenticated.current = true;

    if (!authData || !heapUserProperties) {
      return;
    }

    if (canTrackAnalytics === null) {
      return;
    }

    if (!canTrackAnalytics) {
      if (previousCanTrackAnalytics.current === true) {
        resetHeapIdentity();
      }
      previousCanTrackAnalytics.current = false;
      return;
    }

    previousCanTrackAnalytics.current = true;

    // The `userId` claim is not validated upstream; identifying on a blank value
    // would collapse every such user onto one Heap profile.
    if (!authData.id) {
      return;
    }

    identifyHeapUser(authData.id, heapUserProperties);
  }, [authData, heapUserProperties, isAuthenticated, canTrackAnalytics]);
}
